import { PublicClientApplication, LogLevel, BrowserCacheLocation } from "@azure/msal-browser";

// Valeurs hardcodées pour debug (à remplacer par les valeurs d'environnement)
const CLIENT_ID = "82548af3-e78a-4ac2-b160-d7a2268cc4b4";
const TENANT_ID = "f97e13a1-2cd6-4145-86d6-3e40649b58a0";
const AUTHORITY = "https://login.microsoftonline.com/";

// Détection intelligente du Redirect URI selon l'environnement
const getRedirectUri = () => {
  if (process.env.NODE_ENV === 'development') {
    // En développement, utiliser le port actuel
    const port = window.location.port || '3000';
    const uri = `http://localhost:${port}`;
    return uri;
  } else {
    // En production
    const uri = 'https://transcription.oise-catholique.fr';
    return uri;
  }
};

const REDIRECT_URI = getRedirectUri();

// Détecter si on est sur mobile
const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                 (navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && /MacIntel/.test(navigator.platform));

// **NOUVEAU : Gestionnaire d'état d'interaction global**
class MSALInteractionManager {
  constructor() {
    this.interactionInProgress = false;
    this.pendingRequests = [];
    this.lastInteractionTime = 0;
  }

  async executeWithLock(operation, timeout = 30000) {
    return new Promise((resolve, reject) => {
      const request = { operation, resolve, reject, timestamp: Date.now() };
      
      // Ajouter à la queue
      this.pendingRequests.push(request);
      
      // Configurer le timeout
      const timeoutId = setTimeout(() => {
        const index = this.pendingRequests.indexOf(request);
        if (index > -1) {
          this.pendingRequests.splice(index, 1);
          reject(new Error('MSAL interaction timeout'));
        }
      }, timeout);
      
      // Nettoyer le timeout quand la requête est traitée
      const originalResolve = request.resolve;
      const originalReject = request.reject;
      
      request.resolve = (value) => {
        clearTimeout(timeoutId);
        originalResolve(value);
      };
      
      request.reject = (error) => {
        clearTimeout(timeoutId);
        originalReject(error);
      };
      
      // Traiter la queue
      this.processQueue();
    });
  }

  async processQueue() {
    // Si une interaction est en cours ou pas de requêtes, ne rien faire
    if (this.interactionInProgress || this.pendingRequests.length === 0) {
      return;
    }

    // Vérifier si assez de temps s'est écoulé depuis la dernière interaction
    const timeSinceLastInteraction = Date.now() - this.lastInteractionTime;
    if (timeSinceLastInteraction < 1000) { // Attendre au moins 1 seconde
      setTimeout(() => this.processQueue(), 1000 - timeSinceLastInteraction);
      return;
    }

    // Prendre la première requête
    const request = this.pendingRequests.shift();
    if (!request) return;

    this.interactionInProgress = true;
    this.lastInteractionTime = Date.now();

    try {
      const result = await request.operation();
      request.resolve(result);
    } catch (error) {
      // Si c'est une erreur "interaction_in_progress", nettoyer et réessayer
      if (error.message && error.message.includes('interaction_in_progress')) {
        await this.clearInteractionState();
        
        // Remettre la requête dans la queue pour retry après un délai
        setTimeout(() => {
          this.pendingRequests.unshift(request);
          this.processQueue();
        }, 2000);
      } else {
        request.reject(error);
      }
    } finally {
      this.interactionInProgress = false;
      
      // Traiter la prochaine requête après un délai
      setTimeout(() => this.processQueue(), 500);
    }
  }

  async clearInteractionState() {
    try {
      // Forcer le nettoyage de l'état d'interaction MSAL
      if (window.msal && window.msal.clearCache) {
        await window.msal.clearCache();
      }
      
      // Nettoyer les flags localStorage si ils existent
      localStorage.removeItem('msal.interaction.status');
      localStorage.removeItem('msal.interaction.in.progress');

    } catch (error) {
    }
  }

  reset() {
    this.interactionInProgress = false;
    this.pendingRequests = [];
    this.lastInteractionTime = 0;
  }
}

// Instance globale du gestionnaire
export const msalInteractionManager = new MSALInteractionManager();

// Configuration MSAL optimisée pour mobile et desktop
export const msalConfig = {
  auth: {
    clientId: CLIENT_ID,
    authority: `${AUTHORITY}${TENANT_ID}`,
    redirectUri: REDIRECT_URI,
    // Optimisations pour mobile
    postLogoutRedirectUri: REDIRECT_URI,
    navigateToLoginRequestUrl: !isMobile, // Désactiver sur mobile pour éviter les problèmes de navigation
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage,
    storeAuthStateInCookie: isMobile, // Utiliser les cookies sur mobile pour plus de fiabilité
    secureCookies: window.location.protocol === "https:", // Adapter selon le protocole
    // Optimisations pour mobile
    claimsBasedCachingEnabled: true, // Améliore la gestion du cache
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message) => {
        if (level === LogLevel.Error) {
          // Détecter spécifiquement l'erreur interaction_in_progress
          if (message && message.includes('interaction_in_progress')) {
            setTimeout(() => {
              msalInteractionManager.clearInteractionState();
            }, 1000);
          }
        }
      },
      logLevel: process.env.NODE_ENV === 'production' ? LogLevel.Warning : LogLevel.Verbose,
    },
    // Optimisations pour mobile
    allowNativeBroker: false, // Désactiver le broker natif qui peut causer des problèmes
    windowHashTimeout: isMobile ? 15000 : 6000, // Plus de temps sur mobile
    iframeHashTimeout: isMobile ? 15000 : 6000, // Plus de temps sur mobile
    loadFrameTimeout: isMobile ? 15000 : 6000, // Plus de temps sur mobile
    // ===== NOUVEAU : Optimisations pour le refresh automatique des tokens =====
    tokenRenewalOffsetSeconds: 300, // Rafraîchir le token 5 minutes (300s) avant expiration
    asyncPopups: false, // Désactiver les popups asynchrones pour plus de stabilité
  },
};

// Utiliser les scopes du .env
const GRAPH_SCOPES = process.env.REACT_APP_AZURE_GRAPH_SCOPES
  ? process.env.REACT_APP_AZURE_GRAPH_SCOPES.split(',').map(s => s.trim())
  : ["User.Read"];

// Requêtes optimisées pour mobile et desktop
export const loginRequest = {
  scopes: GRAPH_SCOPES,
  forceRefresh: false,
  prompt: isMobile ? "login" : "select_account", // Sur mobile, utiliser "login" pour éviter les problèmes de cache
  // Optimisations pour mobile
  extraQueryParameters: {},
  ...(isMobile && {
    // Sur mobile, ajouter des paramètres pour améliorer la compatibilité
    loginHint: undefined, // Éviter les hints sur mobile
    domainHint: undefined, // Éviter les hints sur mobile
  })
};

// Configuration pour le refresh silencieux
export const silentRequest = {
  scopes: GRAPH_SCOPES,
  forceRefresh: false, // Permet l'utilisation du cache
  authority: `${AUTHORITY}${TENANT_ID}`,
  // Sur mobile, être plus agressif avec le cache
  ...(isMobile && {
    redirectUri: REDIRECT_URI,
  })
};

/**
 * Créer une requête silencieuse avec le compte utilisateur
 * @param {object} account - Compte MSAL de l'utilisateur
 * @returns {object} Configuration de requête silencieuse optimisée
 */
export const getSilentRequestForAccount = (account) => {
  if (!account) {
    console.warn('[MSAL] Aucun compte fourni pour la requête silencieuse');
    return silentRequest;
  }

  return {
    ...silentRequest,
    account: account, // Inclure le compte améliore considérablement le taux de succès
    forceRefresh: false, // Utiliser le cache si disponible
  };
};

/**
 * Créer une requête SSO silencieuse avec loginHint
 * @param {string} loginHint - Email ou username de l'utilisateur (optionnel)
 * @returns {object} Configuration de requête SSO silencieuse
 */
export const getSsoSilentRequest = (loginHint = null) => {
  const request = {
    scopes: GRAPH_SCOPES,
    authority: `${AUTHORITY}${TENANT_ID}`,
    forceRefresh: false
  };

  // Ajouter le loginHint si disponible pour aider MSAL à trouver le bon compte SSO
  if (loginHint) {
    request.loginHint = loginHint;
  }

  return request;
};

// Initialisation de l'instance MSAL
export const msalInstance = new PublicClientApplication(msalConfig);

// **NOUVEAU : Wrapper sécurisé pour les interactions MSAL**
export const safeMsalLogin = async () => {
  return msalInteractionManager.executeWithLock(async () => {
    return await msalInstance.loginPopup(loginRequest);
  });
};

export const safeMsalAcquireToken = async (request) => {
  return msalInteractionManager.executeWithLock(async () => {
    return await msalInstance.acquireTokenSilent(request);
  });
};

export const safeMsalLoginRedirect = async () => {
  return msalInteractionManager.executeWithLock(async () => {
    return await msalInstance.loginRedirect(loginRequest);
  });
};

export const safeMsalLogoutRedirect = async () => {
  return msalInteractionManager.executeWithLock(async () => {
    return await msalInstance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  });
};

// **NOUVEAU : Wrapper sécurisé pour SSO silencieux**
export const safeMsalSsoSilent = async (request) => {
  return msalInteractionManager.executeWithLock(async () => {
    return await msalInstance.ssoSilent(request);
  });
};

// **AJOUT : Fonction de nettoyage du cache MSAL**
export const clearMSALCache = () => {
  // Nettoyer localStorage
  const localKeysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.includes('msal') || key.includes('login-hint') || key.includes('azure'))) {
      localKeysToRemove.push(key);
    }
  }
  localKeysToRemove.forEach(key => {
    localStorage.removeItem(key);
  });

  // Nettoyer sessionStorage
  const sessionKeysToRemove = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && (key.includes('msal') || key.includes('redirect_attempts'))) {
      sessionKeysToRemove.push(key);
    }
  }
  sessionKeysToRemove.forEach(key => {
    sessionStorage.removeItem(key);
  });
};

// Exposer l'instance à window pour le débogage
window.msal = msalInstance;
window.loginRequest = loginRequest;
window.msalDebug = {
  getAllAccounts: () => msalInstance.getAllAccounts(),
  isInitialized: () => !!msalInstance,
  getGraphScopes: () => loginRequest.scopes,
  interactionManager: msalInteractionManager,
  clearCache: clearMSALCache,
  getRedirectUri: () => REDIRECT_URI,
  getCurrentPort: () => window.location.port
};
